import type { ConfigEntityRootData } from '@web-config-app/core';
import { cloneDeep, set } from 'lodash-es';

/**
 * Alters data for syncing an entity across environments.
 *
 * This function performs the following operations:
 * 1. Makes a deep copy of the entity data
 * 2. Preserve the target environment's status
 *
 * @param {EntityRootData} [params.sourceData] - The source entity data to be synced
 * @param {EntityRootData} [params.targetData] - The target environment's existing entity data
 * @returns {EntityRootData|undefined} - The modified entity data ready for environment sync
 */
export const alterDataForEnvironmentEntitySync = ({
  sourceData,
  targetData,
}: {
  sourceData?: ConfigEntityRootData;
  targetData?: ConfigEntityRootData;
}) => {
  if (typeof sourceData === 'undefined') {
    return undefined;
  }

  // Need to clone the data object because jp.value and unset functions will alter the data passed in.
  const isolatedSourceData = cloneDeep(sourceData);

  // preserve the target environment's entity metadata except for the name and editor state, as the name and editor state are the only fields that should be potentially synced
  const updatedEntityMetadata = {
    ...targetData?.attributes?.entityMetadata,
    name: isolatedSourceData?.attributes?.entityMetadata?.name,
    editorState: isolatedSourceData?.attributes?.entityMetadata?.editorState,
  };

  if (updatedEntityMetadata && isolatedSourceData?.attributes?.entityMetadata) {
    set(isolatedSourceData, 'attributes.entityMetadata', updatedEntityMetadata);
  }

  return isolatedSourceData;
};
